// grok-orders-20261021: Grok-made order recipes (declarative JSON, no code).
// Stored under one key (solcheddar:orderRecipes, cap 50). Saving never arms;
// only the user arms from the BUY long-press picker. Idle recipes cost nothing:
// only the armed one is compiled (compileRecipe) and evaluated.
//
// Recipe = READY then TRIGGER:
// { id, label, desc, by:'grok',
//   settings: [ {key,label,min,max,step,value} ]          (0-2, user adjustable)
//   ready:   { all: [ {ind, win?, op, val} ] }            (0-6, gate only)
//   trigger: {kind:'trade', side:'buy'|'sell', minSol}    (one outside trade >= minSol)
//          | {kind:'liq', dir:'up'|'down', maxSec}        (Liquidity reaction <= maxSec)
//          | {kind:'ard', dir:'LONG'|'PROTECT'}
//   action:  {buy:'default'|SOL} | {sell:pct}
//   then?:   'pushExit'  (after a buy: Push's breakeven sell-into-big-buys) }
// val = number or "$key" of a setting.
import { registerGrokAction } from './grokRegistry'
import { stat } from './signalBus'

export const RECIPES_KEY = 'solcheddar:orderRecipes'
const MAX_RECIPES = 50
const MAX_JSON = 1500

export const INDICATORS = {
  buySol: true, sellSol: true, netSol: true, maxBuy: true, maxSell: true, trades: true, mcapChgPct: true,
  mcap: false, liqAgeSec: false, ardAgeSec: false,                 // true = takes a window
}
const WINS = [10, 30, 60, 300]
const OPS = { '<': (a, b) => a < b, '<=': (a, b) => a <= b, '>': (a, b) => a > b, '>=': (a, b) => a >= b }

const clip = (v, n) => (typeof v === 'string' ? v.replace(/[^\x20-\x7E]/g, '').trim().slice(0, n) : '')
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : (typeof v === 'string' && v.trim() !== '' && Number.isFinite(+v) ? +v : null))

// ── store ──
let cache = null
function load() {
  if (cache) return cache
  try { const a = JSON.parse(localStorage.getItem(RECIPES_KEY) || '[]'); cache = Array.isArray(a) ? a.slice(0, MAX_RECIPES) : [] }
  catch { cache = [] }
  return cache
}
function persist(list) {
  cache = list
  try { localStorage.setItem(RECIPES_KEY, JSON.stringify(list)) } catch {}
  try { window.dispatchEvent(new CustomEvent('solcheddar-order-recipes')) } catch {}
}
export function listRecipes() { return load().slice() }
export function getRecipe(id) { return load().find(r => r.id === id) || null }
export function deleteRecipe(id) {
  const l = load(); const r = l.find(x => x.id === id)
  if (!r) return { ok: false, error: 'not found' }
  if (r.by !== 'grok') return { ok: false, error: 'only Grok-made recipes can be deleted here' }
  persist(l.filter(x => x.id !== id)); return { ok: true }
}
export function updateRecipeSetting(id, key, value) {
  const l = load(); const i = l.findIndex(x => x.id === id); if (i < 0) return
  const r = l[i]; const s = (r.settings || []).find(x => x.key === key); if (!s) return
  const v = num(value); if (v == null) return
  const nv = Math.min(s.max, Math.max(s.min, v))
  const next = { ...r, settings: r.settings.map(x => (x.key === key ? { ...x, value: nv } : x)) }
  const out = l.slice(); out[i] = next; persist(out)
}

// ── validation (untrusted input: whitelist + clamp) ──
export function validateRecipe(raw) {
  try {
    if (!raw || typeof raw !== 'object') return { error: 'recipe must be an object' }
    const label = clip(raw.label, 24); if (!label) return { error: 'label required' }
    const desc = clip(raw.desc, 140)
    const settings = []
    for (const s of (Array.isArray(raw.settings) ? raw.settings : []).slice(0, 2)) {
      const key = clip(s?.key, 16); if (!/^[a-z][a-zA-Z0-9]{0,15}$/.test(key)) return { error: `bad setting key ${key}` }
      let min = num(s.min), max = num(s.max), step = num(s.step), value = num(s.value)
      if (min == null || max == null || !(max > min)) return { error: `setting ${key} needs min < max` }
      if (!(step > 0)) step = (max - min) / 20
      if (value == null) value = min
      settings.push({ key, label: clip(s.label, 16) || key, min, max, step, value: Math.min(max, Math.max(min, value)) })
    }
    const keys = new Set(settings.map(s => s.key))
    const val = (v, name) => {
      if (typeof v === 'string' && v.startsWith('$')) { if (!keys.has(v.slice(1))) throw new Error(`${name}: unknown setting ${v}`); return v }
      const n = num(v); if (n == null) throw new Error(`${name} must be a number or $setting`); return n
    }
    const all = []
    for (const c of (Array.isArray(raw.ready?.all) ? raw.ready.all : []).slice(0, 6)) {
      const ind = c?.ind
      if (!(ind in INDICATORS)) return { error: `unknown indicator ${ind}` }
      if (!(c.op in OPS)) return { error: `bad op ${c.op}` }
      let win = INDICATORS[ind] ? num(c.win) : null
      if (INDICATORS[ind]) win = WINS.includes(win) ? win : 60
      all.push({ ind, ...(win ? { win } : {}), op: c.op, val: val(c.val, `ready ${ind}`) })
    }
    const t = raw.trigger || {}
    let trigger
    if (t.kind === 'trade') {
      if (t.side !== 'buy' && t.side !== 'sell') return { error: 'trade trigger side must be buy or sell' }
      trigger = { kind: 'trade', side: t.side, minSol: val(t.minSol ?? 0, 'minSol') }
    } else if (t.kind === 'liq') {
      if (t.dir !== 'up' && t.dir !== 'down') return { error: 'liq trigger dir must be up or down' }
      trigger = { kind: 'liq', dir: t.dir, maxSec: val(t.maxSec ?? 3, 'maxSec') }
    } else if (t.kind === 'ard') {
      if (t.dir !== 'LONG' && t.dir !== 'PROTECT') return { error: 'ard trigger dir must be LONG or PROTECT' }
      trigger = { kind: 'ard', dir: t.dir }
    } else return { error: 'trigger.kind must be trade, liq or ard' }
    const a = raw.action || {}
    let action
    if (a.buy != null) {
      if (a.buy === 'default') action = { buy: 'default' }
      else { const n = num(a.buy); if (!(n > 0)) return { error: 'action.buy must be "default" or SOL > 0' }; action = { buy: n } }
    } else if (a.sell != null) {
      const n = num(a.sell); if (!(n > 0 && n <= 100)) return { error: 'action.sell must be a percent 1-100' }
      action = { sell: n }
    } else return { error: 'action needs buy or sell' }
    const then = raw.then === 'pushExit' && action.buy != null ? 'pushExit' : undefined
    const id = /^[a-z0-9]{3,16}$/.test(raw.id || '') ? raw.id : 'g' + Date.now().toString(36)
    const recipe = { id, label, desc, by: 'grok', settings, ready: { all }, trigger, action, ...(then ? { then } : {}) }
    if (JSON.stringify(recipe).length > MAX_JSON) return { error: 'recipe too large' }
    return { recipe }
  } catch (e) { return { error: String(e?.message || e) } }
}

export function saveRecipe(raw) {
  const v = validateRecipe(raw); if (v.error) return { ok: false, error: v.error }
  const l = load(); const i = l.findIndex(x => x.id === v.recipe.id)
  if (i >= 0 && l[i].by !== 'grok') return { ok: false, error: 'id taken' }
  if (i < 0 && l.length >= MAX_RECIPES) return { ok: false, error: `limit ${MAX_RECIPES} recipes; delete one first` }
  const out = l.slice(); if (i >= 0) out[i] = v.recipe; else out.push(v.recipe)
  persist(out)
  return { ok: true, id: v.recipe.id, label: v.recipe.label }
}

// ── compile (arm time): plain closures, settings resolved now ──
export function compileRecipe(recipe) {
  const sv = Object.fromEntries((recipe.settings || []).map(s => [s.key, s.value]))
  const V = (v) => (typeof v === 'string' ? Number(sv[v.slice(1)]) : v)
  const conds = (recipe.ready?.all || []).map(c => {
    const f = OPS[c.op], val = V(c.val), ind = c.ind, win = c.win || 60
    return () => f(stat(ind, win), val)
  })
  const ready = conds.length ? () => { for (const c of conds) if (!c()) return false; return true } : () => true
  const t = recipe.trigger
  let trigger
  if (t.kind === 'trade') {
    const side = t.side, min = V(t.minSol)
    trigger = (ev, me) => ev.kind === 'trade' && ev.side === side && ev.sol >= min && !!ev.wallet && ev.wallet !== me
  } else if (t.kind === 'liq') {
    const dir = t.dir, max = V(t.maxSec)
    trigger = (ev) => ev.kind === 'liq' && ev.dir === dir && ev.sec <= max
  } else {
    const dir = t.dir
    trigger = (ev) => ev.kind === 'ard' && ev.dir === dir
  }
  return { ready, trigger }
}

// Compact list for Grok's chat context.
export function recipesForContext() {
  try { return load().map(r => ({ id: r.id, label: r.label, by: r.by })) } catch { return [] }
}

// ── Grok actions (registry; results are not returned to Grok in-turn) ──
try {
  registerGrokAction('save_order_recipe', {
    description: 'Save (create or replace by id) an armed-order recipe in the user\'s BUY long-press order list. Never arms or trades; only the user arms it. Schema in the system prompt (label, desc, settings, ready.all, trigger, action, then).',
    params: {
      type: 'object',
      properties: { recipe: { type: 'object', description: 'the recipe JSON' } },
      required: ['recipe'],
    },
    handler: async (a) => saveRecipe(a?.recipe),
  })
  registerGrokAction('list_order_recipes', {
    description: 'List saved order recipes (id, label, by). Also visible in chart.order_recipes.',
    params: { type: 'object', properties: {} },
    handler: async () => ({ ok: true, recipes: recipesForContext() }),
  })
  registerGrokAction('delete_order_recipe', {
    description: 'Delete an order recipe you (Grok) made, by id.',
    params: { type: 'object', properties: { id: { type: 'string' } }, required: ['id'] },
    handler: async (a) => deleteRecipe(String(a?.id || '')),
  })
} catch {}
